import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Stack,
  Button,
  Box,
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { toast } from 'react-toastify';
import TypeRoleRow from '../../components/typography/TypeRoleRow';
import TypeSpecimenPreview from '../../components/typography/TypeSpecimenPreview';
import SitePreviewFrame, { PREVIEW_PAGES } from '../../components/typography/SitePreviewFrame';
import { ROLE_GROUP_COLORS } from '../../components/typography/roleLabels';
import {
  getTypography,
  saveTypographyDraft,
  publishTypography,
  revertTypography,
  getTypographyHistory,
} from './typographyApi';
import {
  hasBlockingErrors,
  toPayload,
  toGroupsPayload,
  sameRoles,
  sameGroups,
  mergeGroupRoles,
} from './typographyValidation';
import { writeEditorPayload } from '../../utils/typographyPreview';
import { GROUP_SAMPLE_PATH } from '../../utils/typographyGroups';

const STATUS_CHIP = {
  default: { label: "Live: Marketing's defaults", color: 'info' },
  draft: { label: 'Draft saved · not published', color: 'default' },
  published: { label: 'Published', color: 'success' },
  dirty: { label: 'Unpublished changes', color: 'warning' },
};

const ACTION_COLOR = { publish: 'success', revert: 'warning', 'draft-save': 'default' };
const HISTORY_LIMIT = 10;

const fmt = (d) => (d ? new Date(d).toLocaleString() : '');

const SITE = 'site'; // editor scope: site-wide, or a page-group key
const LABELS_PREF_KEY = 'jet-typography-show-labels';

const readLabelsPref = () => {
  try {
    return localStorage.getItem(LABELS_PREF_KEY) !== 'off';
  } catch (e) {
    return true;
  }
};

export default function TypographyDashboard() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [registry, setRegistry] = useState([]);
  const [groups, setGroups] = useState([]);
  const [limits, setLimits] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [breakpoint, setBreakpoint] = useState(900);
  const [server, setServer] = useState(null); // { draft, published, status, publishedAt, publishedBy, ... }
  const [pageGroups, setPageGroups] = useState([]);
  const [form, setForm] = useState({}); // site-wide roles
  const [groupForm, setGroupForm] = useState({}); // { [pageGroup]: { [role]: values } } — overridden roles only
  const [scope, setScope] = useState(SITE);
  const [device, setDevice] = useState('desktop');
  const [previewing, setPreviewing] = useState(false);
  const [previewPath, setPreviewPath] = useState('/home');
  const [previewErrors, setPreviewErrors] = useState([]);
  const [showLabels, setShowLabels] = useState(readLabelsPref);
  const [highlightRole, setHighlightRole] = useState(null);

  const [publishOpen, setPublishOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');

  const [history, setHistory] = useState({ entries: [], total: 0, page: 1, loading: true });

  // What the editor should show for a given server state: the saved draft, else
  // what's live, else Marketing's defaults (no group overrides).
  const baseline = useCallback((s, dflt) => {
    const src = s?.draft || s?.published;
    return src ? { roles: src.roles, groups: src.groups || {} } : { roles: dflt, groups: {} };
  }, []);

  const applyServer = useCallback(
    (data, dflt = defaults) => {
      const next = {
        draft: data.draft,
        published: data.published,
        status: data.status,
        publishedAt: data.publishedAt,
        publishedBy: data.publishedBy,
        updatedAt: data.updatedAt,
        updatedBy: data.updatedBy,
      };
      setServer(next);
      const b = baseline(next, dflt);
      setForm(b.roles);
      setGroupForm(b.groups);
    },
    [baseline, defaults]
  );

  const loadHistory = useCallback(async (page = 1) => {
    setHistory((h) => ({ ...h, loading: true }));
    const res = await getTypographyHistory({ page, limit: HISTORY_LIMIT });
    if (res?.status === 200) {
      setHistory({ entries: res.data.entries || [], total: res.data.total || 0, page, loading: false });
    } else {
      setHistory((h) => ({ ...h, loading: false }));
    }
  }, []);

  useEffect(() => {
    (async () => {
      const res = await getTypography();
      if (res?.status === 200) {
        setRegistry(res.data.roles || []);
        setGroups(res.data.groups || []);
        setPageGroups(res.data.pageGroups || []);
        setLimits(res.data.limits);
        setDefaults(res.data.defaults || {});
        if (res.data.mobileBreakpointPx) setBreakpoint(res.data.mobileBreakpointPx);
        applyServer(res.data, res.data.defaults || {});
      } else {
        toast.error(res?.data?.message || 'Failed to load typography settings');
      }
      setLoading(false);
    })();
    loadHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = useCallback((key, field, value) => {
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  }, []);

  const onGroupChange = useCallback(
    (key, field, value) => {
      setGroupForm((prev) => ({
        ...prev,
        [scope]: { ...prev[scope], [key]: { ...prev[scope]?.[key], [field]: value } },
      }));
    },
    [scope]
  );

  // Overriding starts from the current site-wide values; turning it off drops
  // the override so the role follows the site-wide setting again.
  const toggleOverride = (key, on) => {
    setGroupForm((prev) => {
      const current = { ...(prev[scope] || {}) };
      if (on) current[key] = { ...form[key] };
      else delete current[key];
      return { ...prev, [scope]: current };
    });
  };

  // Clicking a label in the preview jumps to that role's row. Nav/footer/forms
  // only exist on the Site-wide tab, so switch there first if needed.
  const focusRole = useCallback(
    (key) => {
      const role = registry.find((r) => r.key === key);
      if (!role) return;
      if (role.siteWideOnly && scope !== SITE) setScope(SITE);
      setHighlightRole(key);
      setTimeout(() => {
        document.getElementById(`type-role-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 60);
      setTimeout(() => setHighlightRole((cur) => (cur === key ? null : cur)), 2000);
    },
    [registry, scope]
  );

  const toggleLabels = (on) => {
    setShowLabels(on);
    try {
      localStorage.setItem(LABELS_PREF_KEY, on ? 'on' : 'off');
    } catch (e) {
      // Preference just won't persist.
    }
  };

  const changeScope = (next) => {
    setScope(next);
    if (next !== SITE && GROUP_SAMPLE_PATH[next]) setPreviewPath(GROUP_SAMPLE_PATH[next]);
  };

  // The panel previews real pages with the unsaved values; "Open full-size
  // preview" opens /dashboard/typography/preview in a new tab and keeps it in
  // sync with every edit made here.
  const payload = useMemo(
    () => ({ roles: toPayload(form, registry), groups: toGroupsPayload(groupForm, registry) }),
    [form, groupForm, registry]
  );

  const openSitePreview = () => {
    writeEditorPayload(payload);
    setPreviewing(true);
    const qs = new URLSearchParams({ path: previewPath, device });
    window.open(`/dashboard/typography/preview?${qs}`, '_blank', 'noopener');
  };

  useEffect(() => {
    if (!previewing) return undefined;
    const t = setTimeout(() => writeEditorPayload(payload), 250);
    return () => clearTimeout(t);
  }, [previewing, payload]);

  const blocking = useMemo(
    () => (limits ? hasBlockingErrors(form, registry, limits, groupForm) : false),
    [form, groupForm, registry, limits]
  );
  const unsaved = useMemo(() => {
    if (!server || !registry.length) return false;
    const b = baseline(server, defaults);
    return !(sameRoles(form, b.roles, registry) && sameGroups(groupForm, b.groups, registry));
  }, [form, groupForm, server, registry, baseline, defaults]);

  const showErrors = (res, fallback) => {
    if (res?.status === 400 && res.data?.errors) res.data.errors.forEach((m) => toast.error(m));
    else toast.error(res?.data?.message || fallback);
  };

  const doSave = async () => {
    setBusy(true);
    const res = await saveTypographyDraft(payload);
    setBusy(false);
    if (res?.status === 200) {
      applyServer(res.data);
      toast.success('Draft saved');
      loadHistory(1);
    } else {
      showErrors(res, 'Failed to save draft');
    }
  };

  const doPublish = async () => {
    setPublishOpen(false);
    setBusy(true);
    // Save the on-screen values first so publish reflects them.
    const saveRes = await saveTypographyDraft(payload);
    if (saveRes?.status !== 200) {
      setBusy(false);
      showErrors(saveRes, 'Could not save before publishing');
      return;
    }
    const res = await publishTypography();
    setBusy(false);
    if (res?.status === 200) {
      applyServer(res.data);
      toast.success('Published — live on the site now');
      loadHistory(1);
    } else {
      showErrors(res, 'Failed to publish');
    }
  };

  const doRestore = async (entry) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Restore this version? It is published immediately.')) return;
    setBusy(true);
    const res = await revertTypography(entry._id);
    setBusy(false);
    if (res?.status === 200) {
      applyServer(res.data);
      toast.success('Restored and published');
      loadHistory(1);
    } else {
      toast.error(res?.data?.message || 'Failed to restore');
    }
  };

  const doDiscard = () => {
    const b = baseline(server, defaults);
    setForm(b.roles);
    setGroupForm(b.groups);
  };

  const doReset = () => {
    setResetOpen(false);
    setForm(JSON.parse(JSON.stringify(defaults)));
    setGroupForm({});
    toast.info("Marketing's defaults loaded — Save or Publish to keep them");
  };

  const exportText = useMemo(
    () => JSON.stringify({ type: 'jet-typography', version: 2, ...payload }, null, 2),
    [payload]
  );

  const copyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportText);
      toast.success('Copied to clipboard');
    } catch (e) {
      toast.error('Could not copy — select the text and copy it manually');
    }
  };

  const doImport = () => {
    let parsed;
    try {
      parsed = JSON.parse(importText);
    } catch (e) {
      toast.error('That is not valid JSON');
      return;
    }
    // Version 1 exports are a bare roles map; version 2 adds page-group overrides.
    const roles = parsed?.roles && typeof parsed.roles === 'object' ? parsed.roles : parsed;
    const known = registry.filter((r) => roles && typeof roles[r.key] === 'object');
    if (!known.length) {
      toast.error('No typography roles found in that JSON');
      return;
    }
    setForm((prev) => {
      const next = { ...prev };
      known.forEach(({ key }) => {
        next[key] = { ...prev[key], ...roles[key] };
      });
      return next;
    });
    let overrideCount = 0;
    if (parsed?.groups && typeof parsed.groups === 'object') {
      const next = {};
      pageGroups.forEach(({ key: g }) => {
        const src = parsed.groups[g] || {};
        const kept = {};
        registry.forEach(({ key, siteWideOnly }) => {
          if (!siteWideOnly && src[key] && typeof src[key] === 'object') kept[key] = src[key];
        });
        if (Object.keys(kept).length) next[g] = kept;
        overrideCount += Object.keys(kept).length;
      });
      setGroupForm(next);
    }
    setImportOpen(false);
    setImportText('');
    toast.success(
      `Imported ${known.length} roles${overrideCount ? ` and ${overrideCount} group overrides` : ''} — review, then Save or Publish`
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!limits || !server) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Typography settings could not be loaded.</Alert>
      </Container>
    );
  }

  const chip = STATUS_CHIP[server.status] || STATUS_CHIP.default;
  const activeGroup = scope === SITE ? null : pageGroups.find((g) => g.key === scope);
  const historyPages = Math.max(1, Math.ceil(history.total / HISTORY_LIMIT));

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography variant="h4">Typography</Typography>
          <Typography variant="body2" color="text.secondary">
            Site-wide font sizes for every public page. Sizes are in pixels; mobile applies below {breakpoint}px wide.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" color={chip.color} label={chip.label} />
          {unsaved && <Chip size="small" color="warning" variant="outlined" label="Unsaved edits" />}
          {server.publishedAt && (
            <Typography variant="caption" color="text.secondary">
              Published {fmt(server.publishedAt)}
              {server.publishedBy ? ` by ${server.publishedBy}` : ''}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Publishing updates the fonts on every public page (not this admin dashboard) right away. Until something
        is published, the site uses Marketing&apos;s suggested defaults. Use <strong>Save draft</strong> to keep
        work in progress without changing the site. Page groups (the tabs below) can give a set of pages their own
        heading, body, button and hero sizes; navigation, footer and forms are always site-wide.
      </Alert>

      {/* Actions */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
          <Button variant="outlined" onClick={doSave} disabled={busy || blocking}>
            {busy ? 'Working…' : 'Save draft'}
          </Button>
          <Button variant="contained" onClick={() => setPublishOpen(true)} disabled={busy || blocking}>
            Publish
          </Button>
          <Button variant="text" onClick={doDiscard} disabled={busy || !unsaved}>
            Discard changes
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="text" onClick={() => setResetOpen(true)} disabled={busy}>
            Reset to Marketing defaults
          </Button>
          <Button variant="text" onClick={() => setExportOpen(true)}>
            Export
          </Button>
          <Button variant="text" onClick={() => setImportOpen(true)}>
            Import
          </Button>
        </Stack>
        {blocking && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Fix the fields marked in red before saving or publishing.
          </Typography>
        )}
      </Card>

      <Grid container spacing={3}>
        {/* Editor — on large screens it scrolls in its own column so the preview
            stays in view (the dashboard layout's overflow:auto rules out sticky). */}
        <Grid item xs={12} lg={6}>
          <Box
            sx={{
              maxHeight: { lg: 'calc(100vh - 140px)' },
              overflowY: { lg: 'auto' },
              scrollbarGutter: { lg: 'stable' },
              pr: { lg: 1 },
            }}
          >
          <Stack spacing={3}>
            {/* Scope: site-wide, or one page group's overrides */}
            <Card>
              <Tabs
                value={scope}
                onChange={(_, v) => changeScope(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: 1 }}
              >
                <Tab value={SITE} label="Site-wide" sx={{ textTransform: 'none' }} />
                {pageGroups.map((g) => {
                  const count = Object.keys(groupForm[g.key] || {}).length;
                  return (
                    <Tab
                      key={g.key}
                      value={g.key}
                      sx={{ textTransform: 'none' }}
                      label={
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <span>{g.label}</span>
                          {count > 0 && <Chip size="small" color="primary" label={count} sx={{ height: 18 }} />}
                        </Stack>
                      }
                    />
                  );
                })}
              </Tabs>
              {activeGroup && (
                <Alert severity="info" sx={{ m: 2, mt: 1 }}>
                  <strong>{activeGroup.label}:</strong> {activeGroup.pages}. Turn on <strong>Override</strong> for a
                  role to give these pages their own value; everything else follows the site-wide setting.
                </Alert>
              )}
            </Card>

            {groups.map((group) => {
              const roles = registry.filter((r) => r.group === group && (scope === SITE || !r.siteWideOnly));
              if (!roles.length) return null;
              return (
                <Card key={group}>
                  <CardHeader title={group} sx={{ pb: 0 }} />
                  <CardContent sx={{ pt: 1 }}>
                    {roles.map((role) => {
                      if (scope === SITE) {
                        return (
                          <TypeRoleRow
                            key={role.key}
                            role={role}
                            value={form[role.key]}
                            limits={limits}
                            onChange={onChange}
                            highlight={highlightRole === role.key}
                          />
                        );
                      }
                      const override = groupForm[scope]?.[role.key];
                      return (
                        <TypeRoleRow
                          key={role.key}
                          role={role}
                          value={override || form[role.key]}
                          limits={limits}
                          onChange={onGroupChange}
                          disabled={!override}
                          highlight={highlightRole === role.key}
                          action={
                            <FormControlLabel
                              sx={{ ml: 0.5 }}
                              control={
                                <Switch
                                  size="small"
                                  checked={!!override}
                                  onChange={(e) => toggleOverride(role.key, e.target.checked)}
                                />
                              }
                              label={override ? 'Override' : 'Site-wide'}
                            />
                          }
                        />
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}
            {scope !== SITE && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                Navigation, footer and form text are always site-wide.
              </Typography>
            )}
          </Stack>
          </Box>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardHeader
              title="Preview"
              subheader="Your unsaved values on real pages"
              action={
                <Stack direction="row" spacing={1} alignItems="center">
                  <ToggleButtonGroup size="small" exclusive value={device} onChange={(_, v) => v && setDevice(v)}>
                    <ToggleButton value="desktop" sx={{ textTransform: 'none' }}>
                      Desktop
                    </ToggleButton>
                    <ToggleButton value="mobile" sx={{ textTransform: 'none' }}>
                      Mobile
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              }
            />
            <CardContent>
              <TextField
                select
                size="small"
                label="Page"
                value={previewPath}
                onChange={(e) => setPreviewPath(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              >
                {PREVIEW_PAGES.map((pg) => (
                  <MenuItem key={pg.path} value={pg.path}>
                    {pg.label}
                  </MenuItem>
                ))}
                <MenuItem value="sample">All-roles sample (every setting on one page)</MenuItem>
              </TextField>
              {previewPath === 'sample' ? (
                // scrollbarGutter keeps the width fixed when the scrollbar appears,
                // so the scaled sample can't jitter between two sizes.
                <Box sx={{ maxHeight: { lg: 'calc(100vh - 330px)' }, overflowY: 'auto', scrollbarGutter: 'stable' }}>
                  <TypeSpecimenPreview
                    roles={scope === SITE ? form : mergeGroupRoles(form, groupForm[scope])}
                    defaults={defaults}
                    device={device}
                  />
                </Box>
              ) : (
                <SitePreviewFrame
                  settings={payload}
                  path={previewPath}
                  device={device}
                  maxHeight={620}
                  onErrors={setPreviewErrors}
                  showLabels={showLabels}
                  roles={registry}
                  onRoleClick={focusRole}
                />
              )}
              {previewPath !== 'sample' && previewErrors.length > 0 && !blocking && (
                <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 1 }}>
                  Preview paused: {previewErrors[0]}
                </Typography>
              )}
              {previewPath !== 'sample' && (
                <Box sx={{ mt: 1.5 }}>
                  <FormControlLabel
                    control={<Switch size="small" checked={showLabels} onChange={(e) => toggleLabels(e.target.checked)} />}
                    label="Show labels: which setting controls each piece of text"
                    componentsProps={{ typography: { variant: 'body2' } }}
                  />
                  {showLabels && (
                    <>
                      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {Object.entries(ROLE_GROUP_COLORS).map(([name, color]) => (
                          <Stack key={name} direction="row" spacing={0.5} alignItems="center">
                            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
                            <Typography variant="caption" color="text.secondary">
                              {name}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Hover any outlined text for its full name and size; click a label to jump to its setting.
                      </Typography>
                    </>
                  )}
                </Box>
              )}
              <Button size="small" startIcon={<OpenInNewIcon />} sx={{ mt: 1 }} onClick={openSitePreview}>
                Open full-size preview
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* History */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="History" subheader="Every save and publish is kept. Restore republishes an earlier version." />
            {history.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>Who</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell align="right">Restore</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.entries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                          No changes yet.
                        </TableCell>
                      </TableRow>
                    )}
                    {history.entries.map((e) => (
                      <TableRow key={e._id} hover>
                        <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>{fmt(e.createdAt)}</TableCell>
                        <TableCell>{e.user?.name || e.user?.email || 'Unknown'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={e.action} color={ACTION_COLOR[e.action] || 'default'} />
                        </TableCell>
                        <TableCell align="right">
                          {['publish', 'revert'].includes(e.action) && (
                            <Button size="small" startIcon={<RestoreIcon />} onClick={() => doRestore(e)} disabled={busy}>
                              Restore
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {historyPages > 1 && (
              <Stack alignItems="center" py={2}>
                <Pagination count={historyPages} page={history.page} onChange={(_, p) => loadHistory(p)} />
              </Stack>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Publish confirm */}
      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)}>
        <DialogTitle>Publish typography?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This changes the fonts on every public page immediately. The previous version stays in History and can
            be restored.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doPublish}>
            Publish now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset confirm */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle>Reset to Marketing&apos;s defaults?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This loads the suggested type scale into the editor and clears every page-group override. Nothing
            changes on the site until you Save or Publish.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doReset}>
            Load defaults
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Export settings</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Copy these values to move them to another environment (for example, from the dev site to the live
            site), then use Import there.
          </DialogContentText>
          <TextField
            value={exportText}
            multiline
            minRows={10}
            maxRows={18}
            fullWidth
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)}>Close</Button>
          <Button variant="contained" onClick={copyExport}>
            Copy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Import settings</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Paste exported JSON. Values load into the editor for review; nothing changes on the site until you
            Save or Publish.
          </DialogContentText>
          <TextField
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            multiline
            minRows={10}
            maxRows={18}
            fullWidth
            placeholder='{ "type": "jet-typography", "version": 1, "roles": { ... } }'
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={doImport} disabled={!importText.trim()}>
            Load into editor
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
